/**
 * useEngineListExport — eksport widoku listy do CSV dla kartotek NA SILNIKU LIST (E16).
 *
 * Jedno podpięcie per kartoteka: strona podaje adres eksportu, swoje filtry, kolumny tabeli
 * i widoczność kolumn, a hook zwraca gotowe handlery obu akcji katalogu — „Eksport zaznaczonych
 * do CSV" (`export_csv`) i „Eksport listy do CSV" (`export_list_csv`) — do wpięcia
 * w `useListActions({ handlers })`. Przepis na nową kartotekę → `docs/architecture/FRONTEND.md`
 * („Eksport widoku listy do CSV").
 *
 * Kontrakt żądania (jeden adres `GET /api/list-export/<cel>`, rejestr serwera → `runListEngineExport`):
 *  - eksport listy = parametry listy (`buildFilterParams`, ta sama funkcja co zapytanie listy)
 *    + `columns` (slugi widocznych kolumn), BEZ `limit`/`offset`/`ids`;
 *  - eksport zaznaczonych = `ids` + parametry z `selectedKeepParams` (domyślnie firma i sort),
 *    bez filtrów widoku (rekordy są już wybrane).
 * Plik: kolumny widoczne w tabeli, w jej kolejności, z nagłówkami z układu (`customLabel`);
 * tekst komórek z `renderCell` kolumn (`buildExportColumns` dostaje etykiety odwołań z odpowiedzi
 * eksportu — obejmują wszystkie wiersze, nie tylko bieżącą stronę listy).
 *
 * Moduły własne (silnik surowego SQL, inne parametry) korzystają bezpośrednio z
 * `useListCsvExport` — patrz `ModuleRecordsPage`.
 */

import { type FormattingRule, listExportPath, type ListExportTarget } from '@saas/shared';

import type { ColumnDefinition } from '@/components/configurable-table';
import { api } from '@/lib/api';

import { exportColumnsFromTable } from './cell-text';
import type { ListExportResponse } from './run-export';
import { useListCsvExport } from './useListCsvExport';

/** Etykiety odwołań z odpowiedzi eksportu: `{ [slug pola]: { [id]: nazwa } }`. */
export type ExportRefLabels = Record<string, Record<string, string | null>>;

/** Domyślne parametry zachowane przy eksporcie zaznaczonych: wybrana firma i sort listy. */
const DEFAULT_SELECTED_KEEP_PARAMS = ['companyId', 'sort', 'order'] as const;

export interface UseEngineListExportOptions<TRow> {
  /**
   * Kartoteka (cel jednego adresu eksportu `GET /api/list-export/<cel>`), np. `contractors`.
   * Moduły własne idą osobną drogą (`useListCsvExport` + `listExportPath('module', id)`).
   */
  target: Exclude<ListExportTarget, 'module'>;
  /** Podstawa nazwy pliku (bez daty i rozszerzenia). */
  filename: string;
  /** Parametry filtrów listy BEZ stronicowania i projekcji — ta sama funkcja co zapytanie listy. */
  buildFilterParams: () => URLSearchParams;
  /** Parametry zostawiane przy eksporcie zaznaczonych (domyślnie `companyId`, `sort`, `order`). */
  selectedKeepParams?: readonly string[];
  /** Kolumny tabeli strony (te same, które dostaje `ConfigurableTable`). */
  columns: readonly ColumnDefinition<TRow>[];
  /** Widoczne kolumny z `ConfigurableTable.onVisibleColumnsChange`; `null` = jeszcze nieznane. */
  visibleColumnIds: readonly string[] | null;
  /** Ekran gotowy: katalog pól i układ listy wczytane (inaczej lista kolumn bywa niepełna). */
  ready: boolean;
  /** Slug serwera (`?columns=`) dla id kolumny; `undefined` = kolumna bez projekcji. */
  columnSlug: (columnId: string) => string | undefined;
  /** Slugi dołączane zawsze (np. pola czytane przez renderer innej kolumny). */
  extraSlugs?: readonly string[];
  /**
   * Kolumny do składania pliku z etykietami odwołań z odpowiedzi eksportu (te same buildery co
   * tabela, z getterem etykiet zwracającym `refLabels`). Brak → `columns` strony.
   */
  buildExportColumns?: (
    refLabels: ExportRefLabels | undefined,
  ) => readonly ColumnDefinition<TRow>[];
  /** Nadpisania tekstu komórek wg id kolumny (np. komórka z ozdobnikami → sama wartość). */
  textOverrides?: Readonly<Record<string, (row: TRow) => string>>;
  /** Kolumny układu listy (`listLayout.layoutConfig.columns`) — źródło własnych nagłówków. */
  layoutColumns?: ReadonlyArray<{ id: string; customLabel?: string }>;
  /** Reguły formatowania z układu listy (`listLayout.layoutConfig.formattingRules`) → kolory w pliku. */
  formattingRules?: readonly FormattingRule[];
  /** Tytuł listy (nazwa strony) do wiersza tytułu w pliku. */
  heading?: string;
}

export interface UseEngineListExportResult {
  /** Handlery do `useListActions({ handlers })`. */
  handlers: {
    export_csv: (ids: string[]) => Promise<void>;
    export_list_csv: () => Promise<void>;
  };
  isExporting: boolean;
}

export function useEngineListExport<TRow>(
  options: UseEngineListExportOptions<TRow>,
): UseEngineListExportResult {
  const {
    target,
    filename,
    buildFilterParams,
    selectedKeepParams = DEFAULT_SELECTED_KEEP_PARAMS,
    columns,
    visibleColumnIds,
    ready,
    columnSlug,
    extraSlugs = [],
    buildExportColumns,
    textOverrides,
    layoutColumns = [],
    formattingRules,
    heading,
  } = options;

  const listExport = useListCsvExport<TRow>({
    filename,
    formattingRules,
    heading,
    // Lista kolumn ustalana RAZ w chwili kliknięcia (żądanie = plik); ekran niegotowy → null.
    getColumnIds: () =>
      ready && visibleColumnIds !== null
        ? exportColumnsFromTable(columns, visibleColumnIds).map((c) => c.id)
        : null,
    fetchRows: async (ids, columnIds) => {
      const params = buildFilterParams();
      if (ids && ids.length > 0) {
        for (const key of Array.from(params.keys())) {
          if (!selectedKeepParams.includes(key)) params.delete(key);
        }
        params.set('ids', ids.join(','));
      }
      const slugs = [...columnIds.map(columnSlug).filter((s): s is string => !!s), ...extraSlugs];
      if (slugs.length > 0) params.set('columns', Array.from(new Set(slugs)).join(','));
      const res = await api.get<ListExportResponse<TRow>>(
        `${listExportPath(target)}?${params.toString()}`,
      );
      return res.data;
    },
    buildColumns: (response, columnIds) =>
      exportColumnsFromTable(
        buildExportColumns ? buildExportColumns(response.extRefLabels) : columns,
        columnIds,
        {
          textOverrides,
          customLabels: Object.fromEntries(layoutColumns.map((c) => [c.id, c.customLabel])),
        },
      ),
  });

  return {
    handlers: {
      export_csv: listExport.exportSelected,
      export_list_csv: listExport.exportList,
    },
    isExporting: listExport.isExporting,
  };
}
